import { LANGUAGE_STORAGE_KEY, type Language } from "@/i18n/translations";
import { XMarkIcon } from "@heroicons/react/24/outline";
import axios, { AxiosResponse } from "axios";
import { createElement } from "react";
import { toast } from "sonner";
import type { PublicReviewPayload } from "@/types";

// const baseURL = "http://localhost:8080/api/v2";
const baseURL = "https://softiq.cz/api/v2";
// const baseURL = "http://svemar04.local:5000/api/v2";

// 1) Axios instances
const apiClientNoAuth = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status < 500,
});

const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status < 500,
});

// 2) Generic request function types
type Method = "get" | "post" | "put" | "delete";

interface RequestOptions {
  path: string;
  method: Method;
  data?: Record<string, any>;
  params?: Record<string, any>;
  token?: string;
  useToast?: boolean;
  useLoadingToast?: boolean;
  loadingMessage?: string;
}

function getCurrentLanguage(): Language {
  const storedLanguage =
    typeof window !== "undefined"
      ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      : null;

  return storedLanguage === "en" ? "en" : "cs";
}

function getToastCopy() {
  const language = getCurrentLanguage();

  return {
    cs: {
      done: "Hotovo",
      defaultError: "Něco se pokazilo",
      loading: {
        working: "Probíhá zpracování...",
        login: "Probíhá přihlášení k účtu...",
        verify: "Probíhá přihlášení k účtu...",
        profile: "Načítají se informace o vašem profilu...",
        passwordResetRequest: "Odesílá se žádost o obnovu hesla...",
        passwordReset: "Ukládá se nové heslo...",
        register: "Zakládá se nový uživatelský účet...",
        payment: "Dokončuje se aktivace předplatného...",
        paymentVerification: "Ověřuje se stav platby...",
        latestResults: "Načítají se nejnovější výsledky hlídacího psa...",
        watchdogs: "Načítá se seznam hlídacích psů...",
        resultsOffset: "Načítá se další stránka výsledků hlídacího psa...",
        childrenResults: "Načítají se navazující výsledky nabídky...",
        profileSave: "Ukládají se změny profilu...",
        passwordChange: "Mění se heslo k účtu...",
        membershipChange: "Zpracovává se změna předplatného...",
        watchdogCreate: "Zakládá se nový hlídací pes...",
        watchdogSave: "Ukládají se změny hlídacího psa...",
        watchdogDelete: "Maže se hlídací pes a jeho výsledky...",
        favoriteEdit: "Upravuji oblíbené položky...",
        users: "Načítá se seznam aktivních uživatelů...",
        switchUser: "Přepíná se administrátorský náhled uživatele...",
        estateReport: "Načítá se přehled vašich nemovitostí...",
        estateOffers: "Načítají se nabídky k vašim nemovitostem...",
        estateSave: "Ukládá se nová nemovitost do portfolia...",
        estateDelete: "Probíhá mazání nemovitosti z portfolia...",
        transactionsSave: "Ukládají se transakce k nemovitosti...",
        transactionsImport: "Načítá se bankovní výpis...",
        transactionsVerify: "Ověřují se transakce...",
        transactionReminder: "Odesílá se připomínka nájemci...",
        portfolioRecords: "Ukládají se záznamy k nemovitosti...",
        userActivity: "Načítá se aktivita účtu...",
        userActivityDelete: "Maže se aktivita účtu...",
        logs: "Načítají se systémové logy...",
        reports: "Načítají se uložené reporty...",
        testReports: "Načítají se uložené reporty testů...",
        logsDelete: "Probíhá mazání logů...",
        reportsDelete: "Probíhá mazání reportů...",
        support: "Odesílá se zpráva na podporu...",
        unsubscribe: "Probíhá odhlašování z marketingových e-mailů...",
      },
    },
    en: {
      done: "Done",
      defaultError: "Something went wrong",
      loading: {
        working: "Processing...",
        login: "Signing in...",
        verify: "Verifying sign-in details...",
        profile: "Loading your profile...",
        passwordResetRequest: "Sending password reset request...",
        passwordReset: "Saving new password...",
        register: "Creating a new user account...",
        payment: "Completing subscription activation...",
        paymentVerification: "Checking payment status...",
        latestResults: "Loading latest watchdog results...",
        watchdogs: "Loading watchdogs...",
        resultsOffset: "Loading another page of watchdog results...",
        childrenResults: "Loading related listing results...",
        profileSave: "Saving profile changes...",
        passwordChange: "Changing account password...",
        membershipChange: "Processing subscription change...",
        watchdogCreate: "Creating a new watchdog...",
        watchdogSave: "Saving watchdog changes...",
        watchdogDelete: "Deleting watchdog and its results...",
        favoriteEdit: "Updating favorite items...",
        users: "Loading active users...",
        switchUser: "Switching administrator user preview...",
        estateReport: "Loading your property overview...",
        estateOffers: "Loading offers for your properties...",
        estateSave: "Saving new property to portfolio...",
        estateDelete: "Deleting property from portfolio...",
        transactionsSave: "Saving property transactions...",
        transactionsImport: "Reading the bank statement...",
        transactionsVerify: "Verifying transactions...",
        transactionReminder: "Sending a reminder to the tenant...",
        portfolioRecords: "Saving property records...",
        userActivity: "Loading account activity...",
        userActivityDelete: "Deleting account activity...",
        logs: "Loading system logs...",
        reports: "Loading saved reports...",
        testReports: "Loading saved test reports...",
        logsDelete: "Deleting logs...",
        reportsDelete: "Deleting reports...",
        support: "Sending message to support...",
        unsubscribe: "Unsubscribing from marketing e-mails...",
      },
    },
  }[language];
}

function getLoadingMessage(
  key: keyof ReturnType<typeof getToastCopy>["loading"],
) {
  return getToastCopy().loading[key];
}

function getResponseToastMessage(
  responseMessage: unknown,
  fallbackMessage: string,
) {
  if (getCurrentLanguage() === "en") {
    return fallbackMessage;
  }

  return typeof responseMessage === "string" && responseMessage.trim()
    ? responseMessage
    : fallbackMessage;
}

const hiddenLoadingToastIds = new Set<string | number>();

export const dismissLoadingToast = (toastId: string | number) => {
  hiddenLoadingToastIds.delete(toastId);
  toast.dismiss(toastId);
};

export const dismissToastLater = (toastId: string | number) => {
  setTimeout(() => {
    dismissLoadingToast(toastId);
  }, 3000);
};

export const showLoadingToast = (
  message: Parameters<typeof toast.loading>[0],
  options: Parameters<typeof toast.loading>[1] = {},
) => {
  if (options?.id !== undefined && hiddenLoadingToastIds.has(options.id)) {
    return options.id;
  }

  let toastId: string | number;
  toastId = toast.loading(message, {
    ...options,
    dismissible: true,
    cancel: {
      label: createElement(
        "span",
        { "aria-label": "Hide notification" },
        createElement(XMarkIcon, { "aria-hidden": true }),
      ),
      onClick: () => hiddenLoadingToastIds.add(toastId),
    },
  });

  return toastId;
};

const showResponseToast = (
  response: AxiosResponse,
  toastId?: string | number,
  fallbackErrorMessage = getToastCopy().defaultError,
) => {
  const responseMessage = response?.data?.message;
  const copy = getToastCopy();

  if (response.status >= 200 && response.status < 300) {
    toast.success(getResponseToastMessage(responseMessage, copy.done));
  } else {
    toast.error(getResponseToastMessage(responseMessage, fallbackErrorMessage));
  }

  if (toastId !== undefined) {
    dismissToastLater(toastId);
  }
};

const showThrownErrorToast = (
  error: any,
  toastId?: string | number,
  fallbackErrorMessage = getToastCopy().defaultError,
) => {
  const responseMessage = error?.response?.data?.message;
  toast.error(getResponseToastMessage(responseMessage, fallbackErrorMessage));

  if (toastId !== undefined) {
    dismissToastLater(toastId);
  }
};

// 3) Unauthenticated request
export const apiRequestNoAuth = async ({
  path,
  method,
  data,
  params,
  useToast = false,
  useLoadingToast = false,
  loadingMessage,
}: RequestOptions): Promise<AxiosResponse> => {
  const toastId =
    useToast || useLoadingToast
      ? showLoadingToast(loadingMessage ?? getLoadingMessage("working"))
      : undefined;

  try {
    const response = await apiClientNoAuth.request({
      url: path,
      method,
      data,
      params,
    });

    if (useToast) {
      showResponseToast(response, toastId);
    } else if (toastId !== undefined) {
      dismissLoadingToast(toastId);
    }

    return response;
  } catch (error: any) {
    console.error(`API ${method.toUpperCase()} ${path} failed:`, error);
    if (error.response) {
      if (useToast) {
        showResponseToast(error.response, toastId);
      } else if (toastId !== undefined) {
        dismissLoadingToast(toastId);
      }
      return error.response;
    }
    if (useToast) {
      showThrownErrorToast(error, toastId);
    } else if (toastId !== undefined) {
      dismissLoadingToast(toastId);
    }
    throw error;
  }
};

// 4) Authenticated request with Bearer token
export const apiRequest = async ({
  path,
  method,
  data,
  params,
  token,
  useToast = false,
  useLoadingToast = false,
  loadingMessage,
}: RequestOptions): Promise<AxiosResponse> => {
  const toastId =
    useToast || useLoadingToast
      ? showLoadingToast(loadingMessage ?? getLoadingMessage("working"))
      : undefined;

  try {
    const response = await apiClient.request({
      url: path,
      method,
      data,
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (useToast) {
      showResponseToast(response, toastId);
    } else if (toastId !== undefined) {
      dismissLoadingToast(toastId);
    }

    return response;
  } catch (error: any) {
    console.error(`API ${method.toUpperCase()} ${path} failed:`, error);
    if (error.response) {
      if (useToast) {
        showResponseToast(error.response, toastId);
      } else if (toastId !== undefined) {
        dismissLoadingToast(toastId);
      }
      return error.response;
    }
    if (useToast) {
      showThrownErrorToast(error, toastId);
    } else if (toastId !== undefined) {
      dismissLoadingToast(toastId);
    }
    throw error;
  }
};

// 5) Specific API endpoints
export const getVersionFromBe = () => {
  return apiRequestNoAuth({
    path: "/public/version",
    method: "get",
  });
};

export const getLatestBlogFromBe = () => {
  return apiRequestNoAuth({
    path: "/public/blog/latest/get",
    method: "get",
  });
};

export const login = (payload: Record<string, any>) => {
  const { email, password } = payload;
  return apiRequestNoAuth({
    path: "/public/auth/login",
    method: "post",
    data: { email, password },
    useToast: true,
    loadingMessage: getLoadingMessage("login"),
  });
};

export const verify = (payload: Record<string, any>) => {
  const { email, token } = payload;
  return apiRequestNoAuth({
    path: "/public/auth/verify",
    method: "post",
    data: { email, token },
    useToast: true,
    loadingMessage: getLoadingMessage("verify"),
  });
};

export const getUserInfo = (payload: Record<string, any>) => {
  const { email, token } = payload;
  return apiRequest({
    path: "/user/profile/info",
    method: "post",
    data: { email, token },
    token,
    loadingMessage: getLoadingMessage("profile"),
  });
};

export const fetchPromoCodeInfo = (
  promoCode: string,
  email: string,
  token: string,
) => {
  return apiRequest({
    path: "/user/profile/promo-code/info",
    method: "post",
    data: { promoCode, email, token },
    token,
  });
};

export const saveUserPreferences = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/preferences/save",
    method: "post",
    data: payload,
    token,
  });
};

export const resetPasswordRequest = (payload: Record<string, any>) => {
  const { email } = payload;
  return apiRequestNoAuth({
    path: "/public/password/reset/request",
    method: "post",
    data: { email },
    useToast: true,
    loadingMessage: getLoadingMessage("passwordResetRequest"),
  });
};

export const resetPassword = (payload: Record<string, any>) => {
  const { email, newPassword, tokenUrl } = payload;
  return apiRequestNoAuth({
    path: "/public/password/reset",
    method: "post",
    data: { email, newPassword, tokenUrl },
    useToast: true,
    loadingMessage: getLoadingMessage("passwordReset"),
  });
};

export const register = (payload: Record<string, any>) => {
  const { email, password, doRegister } = payload;
  return apiRequestNoAuth({
    path: "/public/register/user",
    method: "post",
    data: { email, password, doRegister },
    useToast: true,
    loadingMessage: getLoadingMessage("register"),
  });
};

export const payment = (payload: Record<string, any>) => {
  const { email, watchDogLevel, token, promoCode } = payload;
  return apiRequest({
    path: "/user/profile/finish",
    method: "post",
    data: { email, watchDogLevel, token, promoCode },
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("payment"),
  });
};

export const paymentVerification = (payload: Record<string, any>) => {
  const { id, parentId, isRegistration } = payload;
  return apiRequestNoAuth({
    path: "/public/payment/check",
    method: "get",
    params: { id, parentId, isRegistration },
    useToast: true,
    loadingMessage: getLoadingMessage("paymentVerification"),
  });
};

export const fetchLatest = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/watchdog/results/latest",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("latestResults"),
  });
};

export const fetchPublicMostExpensiveWeeklyOffers = () => {
  return apiRequestNoAuth({
    path: "/public/watchdog/results/most-expensive-weekly",
    method: "get",
  });
};

export const fetchPublicReviews = () => {
  return apiRequestNoAuth({
    path: "/public/contact/reviews",
    method: "get",
  });
};

export const submitPublicReview = (payload: PublicReviewPayload) => {
  return apiRequestNoAuth({
    path: "/public/contact/review",
    method: "post",
    data: payload,
  });
};

export const fetchWatchDogs = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/watchdog/all",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("watchdogs"),
  });
};

export const fetchResultsByOffest = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/watchdog/results/byOffset",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("resultsOffset"),
  });
};

export const fetchChildrenResults = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/watchdog/results/children",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("childrenResults"),
  });
};

export const updateUserProfile = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/profile/save",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("profileSave"),
  });
};

export const setNewPassword = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/profile/password/change",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("passwordChange"),
  });
};

export const membershipChange = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/profile/membership/change",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("membershipChange"),
  });
};

export const addNewWatchDog = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/watchdog/save",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("watchdogCreate"),
  });
};

export const editWatchDogActivity = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/watchdog/edit",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("watchdogSave"),
  });
};
export const deleteWatchDogCompletely = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/watchdog/delete",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("watchdogDelete"),
  });
};
export const editResult = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/watchdog/results/edit/save",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("favoriteEdit"),
  });
};

export const fetchAllUsers = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/admin/users/active/all",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("users"),
  });
};
export const switchUser = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/admin/users/switch",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("switchUser"),
  });
};

// myEstate
export const fetchEstateReport = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/all",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("estateReport"),
  });
};

export const fetchEstateWatchDogResults = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/watchdogs",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("estateOffers"),
  });
};

export const fetchEstateAnalysis = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/analysis",
    method: "post",
    data: payload,
    token,
  });
};

export const addNewMyEstate = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/estate/save",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("estateSave"),
  });
};

export const deleteMyEstate = (payload: Record<string, any>, token: string) => {
  return apiRequest({
    path: "/user/estate/delete",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("estateDelete"),
  });
};

export const saveTransactions = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/transactions/save",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("transactionsSave"),
  });
};

export const importBankStatement = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/transactions/import",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("transactionsImport"),
  });
};

export const verifyTransactions = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/transactions/verify",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("transactionsVerify"),
  });
};

export const sendTransactionReminder = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/transactions/reminder",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("transactionReminder"),
  });
};

export const fetchPortfolioRecords = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/records/get",
    method: "post",
    data: payload,
    token,
  });
};

export const fetchPortfolioRecordDocumentQuota = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/records/documents/quota",
    method: "post",
    data: payload,
    token,
  });
};

export const savePortfolioRecords = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/estate/records/save",
    method: "post",
    data: payload,
    token,
    useLoadingToast: true,
    loadingMessage: getLoadingMessage("portfolioRecords"),
  });
};

export const fetchUserActivity = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/utils/activity/get",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("userActivity"),
  });
};

export const deleteUserActivity = (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/utils/activity/delete",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("userActivityDelete"),
  });
};

export const fetchLogs = async (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/admin/utils/logs/get",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("logs"),
  });
};

export const fetchReports = async (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/admin/utils/reports/get",
    method: "post",
    data: { email: payload.email, token: payload.token },
    token,
    loadingMessage: getLoadingMessage("reports"),
  });
};

export const fetchReportBody = (email: string, token: string, id: number) => {
  return apiRequest({
    path: "/admin/utils/reports/body/get",
    method: "post",
    data: { email, token, data: { id } },
    token,
  });
};

export const fetchTestReports = async (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/admin/utils/tests/reports/get",
    method: "post",
    data: payload,
    token,
    loadingMessage: getLoadingMessage("testReports"),
  });
};

export const deleteLogs = async (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/admin/utils/logs/delete",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("logsDelete"),
  });
};

export const deleteReports = async (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/admin/utils/reports/delete",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("reportsDelete"),
  });
};

export const featureProposal = async (
  payload: Record<string, any>,
  token: string,
) => {
  return apiRequest({
    path: "/user/utils/support/request",
    method: "post",
    data: payload,
    token,
    useToast: true,
    loadingMessage: getLoadingMessage("support"),
  });
};

export const postFeatureProposal = async (payload: Record<string, any>) => {
  return apiRequestNoAuth({
    path: "/user/utils/support/request",
    method: "post",
    data: payload,
    useToast: true,
    loadingMessage: getLoadingMessage("support"),
  });
};

export const unsubscribeEmail = async (token: string) => {
  return apiRequestNoAuth({
    path: "/public/unsubscribe",
    method: "get",
    params: { token },
    useToast: true,
    loadingMessage: getLoadingMessage("unsubscribe"),
  });
};
